import * as React from "react";
import {
	Alert,
	AlertTitle,
	Box,
	Button,
	Card,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import { BellRingingIcon } from "@phosphor-icons/react/dist/ssr/BellRinging";
import { CopySimpleIcon } from "@phosphor-icons/react/dist/ssr/CopySimple";
import { EyeIcon } from "@phosphor-icons/react/dist/ssr/Eye";
import { LockIcon } from "@phosphor-icons/react/dist/ssr/Lock";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import type { AdminQueueRow, RequestBlocker, RequestState } from "@/hooks/api/use-campaign-requests";
import { campaignRequestsKeys } from "@/hooks/api/use-campaign-requests";
import { useCachedTableStateWithUrl } from "@/hooks/use-cached-table-state-with-url";
import { useDialog } from "@/hooks/use-dialog";
import { useAuth } from "@/components/core/auth-provider";
import { CustomTable } from "@/components/customTable";
import { Column, FilterOption, FilterType } from "@/components/customTable/types";
import { TableAnimation } from "@/components/table-animation/table-animation";

import { StateChip } from "../components";
import {
	formatAge,
	formatDate,
	formatMoney,
	formatMoneyRange,
	REQUEST_BLOCKER_HELP,
	REQUEST_BLOCKER_LABELS,
	REQUEST_STATE_LABELS,
	REQUEST_STATE_OPTIONS,
} from "../constants";
import { labelOf, requestTitle } from "../humanise";
import { BlockedRates, usePlatformRateRows } from "../price-book/blocked-rates";

/**
 * What is sitting with us, per state, when nothing is outstanding on the client's side.
 *
 * `blockedOn` is derived on the API by `blockedOnFor`: every blocker is something only the client
 * can clear, so an open blocker always means the ball is with them and an empty blocker list on a
 * live state means it is with us. That answers *whose* move it is but not *what* the move is, and
 * a queue that only says "us" is a list again — so the state names the work.
 */
const OUR_MOVE: Partial<Record<RequestState, string>> = {
	submitted: "Price it",
	quoted: "Send the quote",
	awaiting_confirmation: "Chase the confirmation",
	confirmed: "Start preparation",
	in_preparation: "Prepare & provision",
};

/**
 * The client-side half of the same question: what they are holding, where no blocker says so.
 *
 * Never the state's own name — the State column beside this one already carries that, and a
 * second cell repeating "Changes requested" tells the reader nothing they did not just read.
 */
const THEIR_MOVE: Partial<Record<RequestState, string>> = {
	draft: "Not sent to us yet",
	changes_requested: "Edits to the request",
};

/**
 * The wording a nudge carries.
 *
 * UX §14 note 3: a nudge "sends the client the same 'waiting on you' language they see in their
 * own list" — so this is built from `REQUEST_BLOCKER_HELP`, which is that language, rather than
 * from a second set of strings that would drift away from it.
 */
function nudgeMessage(row: AdminQueueRow): string {
	const lines: string[] = [
		`Hi — a quick nudge on ${requestTitle(row.title)}${row.clientReference ? ` (${row.clientReference})` : ""}.`,
		"",
	];

	if (row.blockers.length > 0) {
		lines.push("We're waiting on you for:", "");
		for (const blocker of row.blockers) {
			lines.push(`• ${labelOf(REQUEST_BLOCKER_LABELS, blocker)} — ${REQUEST_BLOCKER_HELP[blocker] ?? ""}`);
		}
		lines.push("");
	}

	if (row.blockers.includes("leads_missing") && row.leadsDueDate) {
		lines.push(`You told us the lead list would reach us by ${formatDate(row.leadsDueDate)}.`, "");
	}

	lines.push(
		"Nothing is blocked on our side — everything else is being built in the meantime, and we pick this straight back up when it lands."
	);

	return lines.join("\n");
}

/** What the client is holding, as one grey phrase. Blockers first, then the state as a fallback. */
function theirMoveText(row: AdminQueueRow): string {
	const parts = row.blockers.map((blocker: RequestBlocker) => labelOf(REQUEST_BLOCKER_LABELS, blocker));

	if (parts.length === 0) {
		return THEIR_MOVE[row.state] ?? labelOf(REQUEST_STATE_LABELS, row.state);
	}

	if (row.blockers.includes("leads_missing") && row.leadsDueDate) {
		parts.push(`due ${formatDate(row.leadsDueDate)}`);
	}

	return parts.join(" · ");
}

/**
 * The cross-account queue: every client's requests in one list, newest first.
 *
 * Super-admin only, and gated here as well as on the API. The endpoint reads a public-schema
 * projection no account-scoped role can see, so an account owner reaching this URL gets a 403 —
 * this renders the reason instead, and never issues the call in the first place.
 */
export function AdminQueue(): React.JSX.Element {
	const { user, isLoading } = useAuth();

	if (isLoading) return <QueueShell>{null}</QueueShell>;

	if (!user?.isSuperAdmin) {
		return (
			<QueueShell>
				<Alert severity="warning" icon={<LockIcon />}>
					<AlertTitle>This queue is super-admin only</AlertTitle>
					<Typography variant="body2">
						The queue puts every account&rsquo;s requests in one list, so only a super admin can open it.
					</Typography>
					<Typography variant="body2" sx={{ mt: 1 }}>
						Your own requests are on <strong>Campaign requests</strong> in the sidebar, which shows this account only.
					</Typography>
				</Alert>
			</QueueShell>
		);
	}

	return <AdminQueueTable />;
}

/** The page frame, shared by the queue and by the panel that explains why there isn't one. */
function QueueShell({ children, subtitle }: { children: React.ReactNode; subtitle?: string }): React.JSX.Element {
	return (
		<Box
			sx={{
				maxWidth: "var(--Content-maxWidth)",
				m: "var(--Content-margin)",
				p: "var(--Content-padding)",
				width: "var(--Content-width)",
			}}
		>
			<Stack spacing={4}>
				<Box sx={{ flex: "1 1 auto" }}>
					<Typography variant="h4">Request queue</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
						{subtitle ?? "Every account's campaign requests, newest first"}
					</Typography>
				</Box>
				{children}
			</Stack>
		</Box>
	);
}

function AdminQueueTable(): React.JSX.Element {
	const navigate = useNavigate();

	const {
		items: rows,
		isLoading,
		totalItems,
		refetchData: fetchQueue,
	} = useCachedTableStateWithUrl<AdminQueueRow>({
		queryKey: campaignRequestsKeys.adminQueues(),
		endpoint: "/admin/campaign-requests",
	});

	// Only reached from `AdminQueue`, which has already established the reader is a super admin —
	// `GET /price-book` answers anyone else with a 403.
	const blockingRates = usePlatformRateRows();

	const {
		data: nudging,
		open: isNudgeOpen,
		handleOpen: openNudge,
		handleClose: closeNudge,
	} = useDialog<AdminQueueRow | null>();

	// Page-local on purpose: the API answers a page, not a tally, so counting the whole queue here
	// would be a number made up out of ten rows. Said as "on this page" rather than implied.
	const onUs = rows.filter((row) => row.blockedOn === "us").length;

	const filtersOptions: FilterOption[] = React.useMemo(() => {
		return [
			{
				name: "accountId",
				label: "Client",
				value: "",
				withoutFetchPagination: true,
				type: FilterType.FETCH_SELECT,
				getOptionsUrl: "/accounts",
				fetchLabel: "name",
				fetchValue: "id",
			},
			{
				name: "state",
				label: "State",
				value: [],
				type: FilterType.MULTI_SELECT,
				options: REQUEST_STATE_OPTIONS.map((option) => ({ label: option.label, value: option.value })),
			},
			{
				name: "blockedOn",
				label: "Blocked on",
				value: "",
				type: FilterType.SELECT,
				options: [
					{ label: "Us", value: "us" },
					{ label: "The client", value: "them" },
					// A real answer, and not the same as no filter: dialling, or over.
					{ label: "Nobody", value: "none" },
				],
			},
			{
				name: "leadsOverdue",
				label: "Lead list",
				value: "",
				type: FilterType.SELECT,
				options: [{ label: "Overdue", value: "true" }],
			},
		];
	}, []);

	const columns: Column[] = React.useMemo(() => {
		return [
			{
				// `sortBy` on a header is what makes it clickable, and every one of them names a
				// column the queue can actually order by — the API ignores anything else, so a
				// header that sorted nothing would leave the arrow pointing at an unchanged page.
				header: { id: "title", label: "Campaign", align: "left", sortBy: "title", sx: { fontWeight: "600" } },
				accessorKey: "title",
				getCellValue: (row: AdminQueueRow) => (
					<Stack spacing={0.25}>
						{/* Humanised, because a request created without a name is stored titled after its
						    objective and the raw column value is `appointment_setting`. */}
						<Typography variant="body2" sx={{ fontWeight: 600 }}>
							{requestTitle(row.title)}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{[row.clientReference, `${row.marketCount} market${row.marketCount === 1 ? "" : "s"}`]
								.filter(Boolean)
								.join(" · ")}
						</Typography>
					</Stack>
				),
			},
			{
				// The reason this screen exists: no tenant connection can see two accounts at once,
				// so this column is the only place a client name sits beside another client's.
				//
				// The account and tenant ids used to hang off a hover here. Nobody reads a queue to
				// find a uuid, and a tooltip is the wrong home for one either way: it cannot be
				// copied without dismissing itself, and it hid the only thing this cell is for.
				header: { id: "accountName", label: "Client", align: "left", sortBy: "accountName", sx: { fontWeight: "600" } },
				accessorKey: "accountName",
				getCellValue: (row: AdminQueueRow) =>
					row.accountName ? (
						<Typography variant="body2" sx={{ fontWeight: 600 }}>
							{row.accountName}
						</Typography>
					) : (
						<Typography variant="body2" color="text.secondary">
							Unnamed account
						</Typography>
					),
			},
			{
				header: { id: "state", label: "State", align: "left", sortBy: "state", sx: { fontWeight: "600" } },
				accessorKey: "state",
				getCellValue: (row: AdminQueueRow) => <StateChip state={row.state} variant="admin" />,
			},
			{
				// UX §14 note 1. An accent chip means we are holding it up and it belongs in
				// someone's day; grey text means they are and there is nothing to do but nudge.
				// That one distinction is the difference between a queue and a list.
				// Not sortable: "us", "them" and nobody put in alphabetical order is not an order
				// anyone asked for, and the filter beside it answers the question properly.
				header: { id: "blockedOn", label: "Blocked on", align: "left", sx: { fontWeight: "600" } },
				accessorKey: "blockedOn",
				getCellValue: (row: AdminQueueRow) => {
					if (row.blockedOn === "us") {
						return (
							<Chip
								color="primary"
								variant="soft"
								size="small"
								label={OUR_MOVE[row.state] ?? labelOf(REQUEST_STATE_LABELS, row.state)}
							/>
						);
					}

					if (row.blockedOn === "them") {
						return (
							<Typography variant="body2" color="text.secondary">
								{theirMoveText(row)}
							</Typography>
						);
					}

					return (
						<Typography variant="body2" color="text.secondary">
							—
						</Typography>
					);
				},
			},
			{
				// UX §14 note 2: age is on every row so nothing rots quietly. We committed to no
				// SLA — this is how that stays honest anyway. The queue opens newest first, so
				// this header is also the way back to what has been waiting longest: descending
				// age is the oldest work, which is the reading the column was put here for.
				header: { id: "age", label: "Age", align: "left", sortBy: "age", sx: { fontWeight: "600" } },
				accessorKey: "ageSeconds",
				getCellValue: (row: AdminQueueRow) => {
					if (row.blockedOn === null) {
						return (
							<Typography variant="body2" color="text.secondary">
								—
							</Typography>
						);
					}

					// The clock the age is counted from, printed under it rather than hidden behind a
					// hover: "4h" is only actionable next to what it is four hours since. It says
					// exactly what the API measured — `now() - COALESCE(submitted_at, created_at)` —
					// so a caption here can never disagree with the number above it.
					const since = row.submittedAt
						? `since submitted ${formatDate(row.submittedAt)}`
						: `since raised ${formatDate(row.createdAt)}`;

					return (
						<Stack spacing={0.25}>
							<Typography
								variant="body2"
								sx={{ fontWeight: row.blockedOn === "us" ? 600 : 400 }}
								color={row.blockedOn === "us" ? "text.primary" : "text.secondary"}
							>
								{formatAge(row.ageSeconds)}
							</Typography>
							<Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
								{since}
							</Typography>
						</Stack>
					);
				},
			},
			{
				header: { id: "value", label: "Value", align: "left", sortBy: "value", sx: { fontWeight: "600" } },
				accessorKey: "value",
				getCellValue: (row: AdminQueueRow) => {
					const currency = row.currency ?? "SGD";

					if (!row.value) {
						return (
							<Typography variant="body2" color="text.secondary">
								Not priced
							</Typography>
						);
					}

					// The split under the total rather than behind a hover. The headline is the
					// midpoint of a range, and a midpoint printed alone is the one number on this
					// screen that reads as firmer than it is.
					return (
						<Stack spacing={0.25}>
							<Typography variant="body2" sx={{ fontWeight: 600 }}>
								{formatMoney(row.value, currency)}
							</Typography>
							<Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
								{`${formatMoney(row.fixedTotal, currency)} fixed · ${formatMoneyRange(
									row.variableLow,
									row.variableHigh,
									currency
								)} est.`}
							</Typography>
						</Stack>
					);
				},
			},
			{
				header: { id: "actions", label: "", width: 130, align: "left", sx: { fontWeight: "600" } },
				accessorKey: "actions",
				getCellValue: (row: AdminQueueRow) => (
					<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
						{row.blockedOn === "them" ? (
							<Button size="small" variant="outlined" startIcon={<BellRingingIcon />} onClick={() => openNudge(row)}>
								Nudge
							</Button>
						) : (
							<Button
								size="small"
								variant={row.blockedOn === "us" ? "contained" : "outlined"}
								startIcon={row.blockedOn === "us" ? undefined : <EyeIcon />}
								onClick={() => navigate(`/platform/campaign-requests/${row.requestId}`)}
							>
								{row.blockedOn === "us" ? "Open" : "View"}
							</Button>
						)}
					</Box>
				),
			},
		];
	}, [navigate, openNudge]);

	return (
		<React.Fragment>
			<QueueShell
				subtitle={`Every account's campaign requests, newest first — ${totalItems} in the queue, ${onUs} on this page waiting on us`}
			>
				{/* Above the queue, because it is the reason half of it is stuck.
				    An unconfirmed platform rate blocks every quote for every client — a request
				    that needs one never leaves "submitted", so it shows up here as a row waiting
				    on us with no obvious cause. This names the cause. */}
				<BlockedRates
					rows={blockingRates}
					context="queue"
					action={
						<Button color="inherit" size="small" onClick={() => navigate("/platform/price-book")}>
							Open the Price Book
						</Button>
					}
				/>

				<TableAnimation delay={0}>
					<Card>
						<CustomTable
							columns={columns}
							filtersOptions={filtersOptions}
							data={rows}
							refetchData={fetchQueue}
							isLoading={isLoading}
							totalItems={totalItems}
							onRowClick={(row: AdminQueueRow) => navigate(`/platform/campaign-requests/${row.requestId}`)}
						/>
					</Card>
				</TableAnimation>
			</QueueShell>

			<NudgeDialog open={isNudgeOpen} row={nudging ?? null} onClose={closeNudge} />
		</React.Fragment>
	);
}

/**
 * One click, the client's own words — and an honest account of what this button can and cannot do.
 *
 * There is no nudge endpoint on the API: nothing under `campaign-requests` sends a client any
 * message, and the only thing the backend knows about nudging is the `leadsOverdue` filter that
 * says which requests are due one. So this composes the wording and hands it over rather than
 * claiming to have sent it — a "Sent" toast over an API that sent nothing is the one outcome
 * worse than a manual step.
 */
function NudgeDialog({
	open,
	row,
	onClose,
}: {
	open: boolean;
	row: AdminQueueRow | null;
	onClose: () => void;
}): React.JSX.Element | null {
	if (!row) return null;

	const message = nudgeMessage(row);

	const handleCopy = async (): Promise<void> => {
		try {
			await globalThis.navigator.clipboard.writeText(message);
			toast.success("Nudge copied");
		} catch {
			toast.error("Could not reach the clipboard — select the text and copy it");
		}
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>
				Nudge {row.accountName ?? "the client"}
				<Typography variant="body2" color="text.secondary">
					{requestTitle(row.title)} · waiting {formatAge(row.ageSeconds)}
				</Typography>
			</DialogTitle>

			<DialogContent>
				<Stack spacing={2} sx={{ pt: 1 }}>
					<Alert severity="info">
						<AlertTitle>Nothing is sent from here yet</AlertTitle>
						<Typography variant="body2">
							Copy the wording below and send it yourself — it is the same &ldquo;waiting on you&rdquo; language the
							client reads on their own list.
						</Typography>
					</Alert>

					{/* No label: the dialog is titled "Nudge <client>" and the alert above says to copy
					    the wording below, so a "Message" caption on the box holding it is the third
					    time in four inches this says the same word. */}
					<TextField
						multiline
						fullWidth
						minRows={8}
						value={message}
						slotProps={{ input: { readOnly: true }, htmlInput: { "aria-label": "Nudge message" } }}
					/>
				</Stack>
			</DialogContent>

			<DialogActions>
				<Button color="secondary" onClick={onClose}>
					Close
				</Button>
				<Button variant="contained" startIcon={<CopySimpleIcon />} onClick={handleCopy}>
					Copy
				</Button>
			</DialogActions>
		</Dialog>
	);
}
